import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { fromLonLat } from 'ol/proj.js';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAllPoints,
  selectEditorEdgeStartNode,
  selectEditorMode,
  selectFilteredNodes,
  selectEditorMoveNode,
} from '../map/slice/mapSelectors';
import * as selectedItemsActions from '../selectedItems/selectedItemsSlice';
import { colors, getNodeColor } from '../utils/colors';
import { MapTooltip } from './MapTooltip';
import { MapPopup, MapPopupRef } from './MapPopup';
import { NodePopup } from '../nodes/NodePopup';
import {
  selectEditState,
  selectMarkets,
  selectPointConstraints,
  selectScenario,
  selectTrades,
} from '../scenarios/scenarioSlice';
import { selectActivePath } from '../paths/pathsSlice';
import { NodeTooltip } from '../nodes/NodeTooltip';
import {
  selectNodeSize,
  selectPlotterMarkets,
} from '../plotter/plotterSelectors';
import { mapActions } from './slice/mapSlice';
import { Theme, useTheme } from '../helpers/hooks';
import { FlatStyleLike } from 'ol/style/flat';
import { Node, OperationChainLink, Point } from '@/types';
import { useMapLayer } from '../hooks/useMapLayer';
import { isCompressor } from '../utils/pointUtils';
import { FeatureLike } from '../utils/geoJsonUtils';
import { useMapZoom } from '../hooks/useMapZoom';
import { useMapContext, MapEventListener } from './context/MapContext';
import { useAnalytics } from '../hooks/useAnalytics';
import { AnalyticsEvent } from '../analytics';

const DETAILED_VIEW_ZOOM_THRESHOLD = 8;

const getNodeStyle = (size: number, theme: Theme): FlatStyleLike => [
  {
    filter: ['==', ['get', 'compressor'], 0],
    style: {
      'circle-radius': size,
      'circle-fill-color': ['get', 'color'],
      'circle-rotate-with-view': false,
      'circle-displacement': [0, 0],
      'circle-opacity': 0.8,
    },
  },
  {
    filter: ['==', ['get', 'compressor'], 1],
    style: {
      'shape-points': 4,
      'shape-radius': size * 1.6,
      'shape-fill-color': theme === 'light' ? '#ffffff' : '#262626',
      'shape-stroke-color': ['get', 'compressorStroke'],
      'shape-stroke-width': 2,
      'shape-rotate-with-view': false,
      'shape-displacement': [0, 0],
      'shape-opacity': 1,
      'shape-angle': Math.PI / 4,
    },
  },
];

const nodeToGEOJson = (
  node: Node,
  color: string,
  points: Record<string, Point> = {},
): FeatureLike => {
  const isCs = isCompressor(node, points);
  const verified =
    isCs &&
    node.points
      .map((pointId) => points[pointId])
      .filter(Boolean)
      .every((point) => point.loc_type_ind !== 'COM' || point.verified);
  const geometry = {
    type: 'Point',
    coordinates: fromLonLat([Number(node.lng ?? 0), Number(node.lat ?? 0)]),
  };

  return {
    type: 'Feature',
    geometry,
    properties: {
      id: node.id,
      key: `node-${node.id}`,
      compressor: isCs ? 1 : 0,
      color: color,
      compressorStroke: verified
        ? colors.elements.verifiedCompressorBorder
        : colors.elements.unverifiedCompressorBorder,
    },
  };
};

export type MapNodesProps = {
  zIndex: number;
};

export const MapNodes: React.FC<MapNodesProps> = ({ zIndex }) => {
  const analytics = useAnalytics();
  const dispatch = useDispatch();
  const [theme] = useTheme();
  const {
    registerHoverListener,
    unregisterHoverListener,
    registerClickListener,
    unregisterClickListener,
  } = useMapContext();
  const nodes = useSelector(selectFilteredNodes);
  const points = useSelector(selectAllPoints);
  const nodeSize = useSelector(selectNodeSize);
  const activelySelectedNodeIds = useSelector(
    selectedItemsActions.selectActivelySelectedNodeIds,
  );

  const pointConstraints = useSelector(selectPointConstraints);
  const markets = useSelector(selectMarkets);
  const trades = useSelector(selectTrades);
  const plotterMarkets = useSelector(selectPlotterMarkets);
  const path = useSelector(selectActivePath);
  const scenario = useSelector(selectScenario);

  const marketsAndTrades = useMemo(
    () => [...markets, ...plotterMarkets, ...trades],
    [markets, trades, plotterMarkets],
  );

  const isEditing = useSelector(selectEditState);
  const isScenarioPage =
    location.pathname.match(/\/scenarios\/\d+$/)?.some((match) => match) ??
    false;
  const isMapBuilderPage = location.pathname.includes('/map-builder');

  const popupRef = useRef<MapPopupRef>(null);

  const [tooltipNode, setTooltipNode] = useState<Node | null>(null);
  const [popupNode, setPopupNode] = useState<Node | null>(null);
  const zoom = useMapZoom();

  const showDetailedView = zoom > DETAILED_VIEW_ZOOM_THRESHOLD;

  const selectedPoint = useSelector(selectedItemsActions.selectActivePoint);
  const editorMode = useSelector(selectEditorMode);
  const editorEdgeStartNode = useSelector(selectEditorEdgeStartNode);
  const editorMoveNode = useSelector(selectEditorMoveNode);

  const areNodesVisible = nodeSize > 0;

  const hoverListener: MapEventListener = useCallback(
    (feature) => {
      if (feature?.getProperties()?.key === `node-${tooltipNode?.id}`) return;
      if (!feature || !feature?.getProperties()?.key?.startsWith('node-')) {
        setTooltipNode(null);
        return;
      }
      const nodeId = feature.getProperties()?.id;
      const node = nodes?.[nodeId];
      setTooltipNode(node);
    },
    [nodes],
  );

  const closePopup = useCallback(() => {
    setPopupNode(null);
    popupRef.current?.hide();
  }, [popupRef, setPopupNode]);

  const clickListener: MapEventListener = useCallback(
    (feature) => {
      const nodeId =
        feature && feature.getProperties()?.key?.startsWith('node-')
          ? feature.getProperties()?.id
          : null;
      const node = nodes?.[nodeId];
      const isCreatingEdge = Boolean(editorEdgeStartNode);
      if (!node || isCreatingEdge) {
        popupRef.current?.hide();
        return;
      }
      if (isMapBuilderPage && editorMode === 'shift') {
        dispatch(mapActions.setEditorEdgeStartNode(node.id));
        return;
      }
      analytics.logEvent(AnalyticsEvent.CLICKED_NODE, {
        node: JSON.stringify(node),
      });
      popupRef.current?.show(fromLonLat([Number(node.lng), Number(node.lat)]));
      dispatch(selectedItemsActions.markerClicked(node.id));
      setPopupNode(node);
    },
    [
      nodes,
      isMapBuilderPage,
      editorMode,
      editorEdgeStartNode,
      dispatch,
      setPopupNode,
      popupRef,
    ],
  );

  useEffect(() => {
    if (selectedPoint) {
      const node = nodes[selectedPoint.node ?? ''];
      if (!node) return;
      setPopupNode(node);
      popupRef.current?.show(fromLonLat([Number(node.lng), Number(node.lat)]));
    }
  }, [selectedPoint, nodes, popupRef]);

  const nodeFeatures = useMemo(() => {
    return areNodesVisible
      ? Object.values(nodes)
          .filter((node) => {
            if (Boolean(node) === false) return false;
            if (node.id.toString() === editorMoveNode?.toString()) return false;
            return (
              showDetailedView || isMapBuilderPage || isCompressor(node, points)
            );
          })
          .map((node) =>
            nodeToGEOJson(
              node,
              getNodeColor(node, points, activelySelectedNodeIds),
              points,
            ),
          )
      : [];
  }, [
    nodes,
    points,
    areNodesVisible,
    showDetailedView,
    isMapBuilderPage,
    editorMoveNode,
    activelySelectedNodeIds,
  ]);

  const style = useMemo(() => getNodeStyle(nodeSize, theme), [nodeSize, theme]);

  useMapLayer(nodeFeatures, { zIndex, style });

  useEffect(() => {
    registerHoverListener(hoverListener);
    return () => {
      unregisterHoverListener(hoverListener);
    };
  }, [hoverListener, registerHoverListener, unregisterHoverListener]);

  useEffect(() => {
    registerClickListener(clickListener);
    return () => {
      unregisterClickListener(clickListener);
    };
  }, [clickListener, registerClickListener, unregisterClickListener]);

  const tooltipPoints = useMemo(() => {
    if (!tooltipNode) return [];
    return tooltipNode.points.map((pointId) => points[pointId]).filter(Boolean);
  }, [tooltipNode, points]);

  const constraintsOnPoint = useMemo(() => {
    return Object.values(pointConstraints).filter((constraint) =>
      tooltipNode?.points.includes(constraint.point),
    );
  }, [pointConstraints, tooltipNode]);

  const pricesOnPoint = useMemo(() => {
    return marketsAndTrades.filter((market) =>
      tooltipNode?.points.includes(market.point),
    );
  }, [marketsAndTrades, tooltipNode]);

  const operations = useMemo(() => {
    if (!isScenarioPage || isEditing || !path) return {};
    if (!path.operation_chain) {
      console.warn(`Could not find operations for path ${path.id}`);
      return {};
    }
    const pathOperations = path.operation_chain.chain_links
      .filter((chain_link: OperationChainLink) => chain_link.has_flow == true)
      .map((chain_link: OperationChainLink) => {
        // this is basically copied from ChainDetails, maybe it could be extracted for reuse
        const operation = scenario.operations.find(
          (op) => op.id === chain_link.operation_id,
        );
        return {
          ...operation,
          chain_link,
        };
      });

    return pathOperations.reduce((acc: Record<string, any[]>, op: any) => {
      if (!op.delivery_point || !op.receipt_point) {
        return acc;
      }
      if (!acc[op.delivery_point.node]) {
        acc[op.delivery_point.node] = [];
      }
      acc[op.delivery_point.node].push({
        description: op.chain_link.delivery_description,
        type: 'Delivery',
        index: op.chain_link.order,
      });

      if (!acc[op.receipt_point.node]) {
        acc[op.receipt_point.node] = [];
      }
      acc[op.receipt_point.node].push({
        description: op.chain_link.receipt_description,
        type: 'Receipt',
        index: op.chain_link.order,
      });
      return acc;
    }, {});
  }, [path, scenario, isScenarioPage, isEditing]);

  return (
    <>
      <MapTooltip visible={Boolean(tooltipNode)}>
        <NodeTooltip
          node={tooltipNode}
          points={tooltipPoints}
          prices={pricesOnPoint}
          constraints={constraintsOnPoint}
          operations={operations[tooltipNode?.id ?? ''] ?? []}
          isScenarioPage={isScenarioPage}
          isEditing={isEditing}
        />
      </MapTooltip>
      <MapPopup ref={popupRef} onClose={closePopup} autoPan={true}>
        <NodePopup nodeId={popupNode?.id} closePopup={closePopup} />
      </MapPopup>
    </>
  );
};
